import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CurrencySelect } from '../CurrencySelect';
import { money } from './poShared';

export const fetchRate = async (api, cur) => {
  if (!cur || cur === 'CAD') return '1';
  try { const r = await api().get(`/currency/rates/${cur}/CAD`); return r.data?.rate ? String(Number(r.data.rate).toFixed(6)) : '1'; } catch { return '1'; }
};

// MOQ / order-multiple problems for one PO line against its item master.
export const lineOrderingIssue = (l, it) => {
  if (!it) return null;
  const q = Number(l.quantity || 0);
  if (it.min_order_qty && q < Number(it.min_order_qty)) return `Below item MOQ of ${it.min_order_qty}`;
  if (it.order_multiple && q > 0 && Math.abs(q / Number(it.order_multiple) - Math.round(q / Number(it.order_multiple))) > 1e-9) return `Not a multiple of ${it.order_multiple}`;
  return null;
};

// Currency selector + vendor ordering notes + min-order / MOQ warnings (warnings never block saving).
export const PoOrderingBar = ({ form, setForm, vendor, items, subtotal, editable }) => {
  const { api } = useAuth();
  const changeCur = async (cur) => { setForm(f => ({ ...f, currency: cur })); const rate = await fetchRate(api, cur); setForm(f => ({ ...f, exchange_rate: rate })); };
  const cur = form.currency || 'CAD';
  const belowMin = vendor?.min_order_value && subtotal < Number(vendor.min_order_value);
  const issues = form.lines.map((l, i) => [i, lineOrderingIssue(l, items.find(x => x.id === l.item_id))]).filter(([, m]) => m);
  return (
    <div className="space-y-2" data-testid="po-ordering-bar">
      <div className="flex items-end gap-3 flex-wrap">
        <div><Label className="text-xs">Currency</Label><CurrencySelect value={cur} onChange={changeCur} disabled={!editable} testId="po-currency-select" /></div>
        {cur !== 'CAD' && (
          <div><Label className="text-xs">1 {cur} = ? CAD</Label><Input type="number" min="0" step="0.000001" value={form.exchange_rate} onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })} disabled={!editable} className="w-32 h-9 rounded-sm mono" data-testid="po-exchange-rate" /></div>
        )}
        {cur !== 'CAD' && <div className="text-xs text-slate-500 pb-2" data-testid="po-cad-equivalent">≈ {money(subtotal * Number(form.exchange_rate || 1))} CAD before tax</div>}
      </div>
      {vendor?.ordering_notes && (
        <div className="flex items-start gap-2 text-xs px-3 py-2 rounded-sm bg-blue-50 border border-blue-200 text-blue-900" data-testid="po-vendor-ordering-notes"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span><b>{vendor.vendor_name}:</b> {vendor.ordering_notes}</span></div>
      )}
      {(belowMin || issues.length > 0) && (
        <div className="text-xs px-3 py-2 rounded-sm bg-amber-50 border border-amber-200 text-amber-900 space-y-0.5" data-testid="po-ordering-warnings">
          {belowMin && <div className="flex items-center gap-1.5" data-testid="po-below-vendor-min"><AlertTriangle className="w-3.5 h-3.5" /> Subtotal {money(subtotal)} is below {vendor.vendor_name}'s minimum order of {money(vendor.min_order_value)}.</div>}
          {issues.map(([i, m]) => <div key={i} className="flex items-center gap-1.5" data-testid={`po-line-moq-warning-${i}`}><AlertTriangle className="w-3.5 h-3.5" /> Line {i + 1}: {m}.</div>)}
        </div>
      )}
    </div>
  );
};

export default PoOrderingBar;
